import { Search } from "lucide-react";
import { useState, useEffect } from "react";
import { Card, CardContent } from "../../ui/card.tsx";
import { Input } from "../../ui/input.tsx";
import { useSubAccounts } from "../../../hooks/useSubAccounts.ts";
import { Profile } from "../../../types/subaccounts.ts";
import { useDebounce } from "../../../hooks/useDebounce.ts";
// import { format } from 'date-fns';
import { Table, TableHeader, TableRow, TableHead, TableCell, TableBody } from "../../ui/table.tsx";
import { NoAccounts } from "./NoAccounts.tsx";
import { ErrorLoading } from "./ErrorLoadingAccounts.tsx";
import { CreateSubAccount } from "./CreateSubAccount.tsx";
import SettingsDialog from "./SettingsDialog.tsx";
import { usePartnerSubAccountCreation } from "../../../hooks/usePartnerSubAccountCreation.ts";
import ActionsMenu from "./ActionsMenu.tsx";
import DeleteDialog from "./DeleteDialog.tsx";
import RestoreDialog from "./RestoreDialog.tsx";
import { TooltipInfo } from "../../ui/TooltipInfo.tsx";

export const AccountsTable = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isRestoreOpen, setIsRestoreOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Profile | null>(null);
  const [displayedAccounts, setDisplayedAccounts] = useState<Profile[] | undefined>(undefined);

  const debouncedSearchQuery = useDebounce(searchQuery, 300); // 300ms debounce

  // Use the same hook that CreateSubAccount uses (which works correctly)
  const { availablePlans } = usePartnerSubAccountCreation();


  const { data: accounts, isLoading, error } = useSubAccounts(debouncedSearchQuery);

  // Keep previous results visible while loading new ones
  useEffect(() => {
    if (!isLoading && accounts !== undefined) {setDisplayedAccounts(accounts);}
  }, [accounts, isLoading]);
  
  const handleOpenSettings = (account: Profile) => {
    setSelectedAccount(account);
    setIsSettingsDialogOpen(true);
    setOpenDropdownId(null);
  };

  const handleOpenDeleteDialog = (account: Profile) => {
    setSelectedAccount(account);
    setIsDeleteOpen(true);
    setOpenDropdownId(null);
  };

  const handleOpenRestoreDialog = (account: Profile) => {
    setSelectedAccount(account);
    setIsRestoreOpen(true);
    setOpenDropdownId(null);
  };
  
  // UI-only: retention window for soft-deleted users (months)
  const RETENTION_MONTHS = 6;
  const MANUAL_HARD_DELETE_AFTER_MONTHS = 2;
  
  return (
    <Card>
      <CardContent className="p-0">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-[200px]">
              <Search className="absolute size-4 left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <CreateSubAccount />
          </div>
        </div>

        {(!displayedAccounts || displayedAccounts?.length === 0) && !isLoading && <NoAccounts searchQuery={searchQuery} />}
        {error && <ErrorLoading error={error.message} />}
        {displayedAccounts && displayedAccounts.length > 0 && (
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                {['Customer', 'Plan', 'Actions'].map((header) => (
                  <TableHead className={header === 'Actions' ? 'text-right' : 'text-left'}>{header}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedAccounts?.map((account) => {
                const isDeleted = account.deleted_at !== null;
                const deletedAt = account.deleted_at ? new Date(account.deleted_at) : null;
                const now = new Date();
                const monthsSinceDelete = deletedAt ? ((now.getFullYear() - deletedAt.getFullYear()) * 12 + (now.getMonth() - deletedAt.getMonth()) + (now.getDate() >= deletedAt.getDate() ? 0 : -1)) : 0;
                const canPermanentDelete = isDeleted && monthsSinceDelete >= MANUAL_HARD_DELETE_AFTER_MONTHS;
                const msUntil2Months = deletedAt ? (new Date(deletedAt.getTime()).setMonth(deletedAt.getMonth() + MANUAL_HARD_DELETE_AFTER_MONTHS) as unknown as number) - now.getTime() : 0;
                const daysRemaining = Math.max(Math.ceil(msUntil2Months / (24*60*60*1000)), 0);
                return (
                  <TableRow key={account.user_id} className={isDeleted ? "bg-muted/30" : ""}>
                    <TableCell className={`text-sm ${isDeleted ? 'text-muted-foreground line-through' : 'text-muted-foreground'}`}>
                      {account.email}
                      {isDeleted && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-black font-medium no-underline">
                          (Soft Deleted)
                          <TooltipInfo description={`Soft-deleted users can be permanently deleted after ${MANUAL_HARD_DELETE_AFTER_MONTHS} months, and are auto-removed after ${RETENTION_MONTHS} months. You can restore the account anytime before permanent deletion.`} />
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={`text-sm ${isDeleted ? 'text-muted-foreground line-through' : 'text-muted-foreground'}`}>
                      {(() => {
                        const plan = account.subscription_plans?.[0] as any;
                        const planName = plan?.name || account.subscription_plan_identifier || '—';
                        const price = plan?.price_monthly ?? null;
                        return price !== null && price !== undefined
                          ? `${planName} ($${price}/mo)`
                          : planName;
                      })()}
                    </TableCell>
                    <TableCell >
                      <ActionsMenu
                        isOpen={openDropdownId === account.user_id}
                        setIsOpen={(open) => setOpenDropdownId(open ? account.user_id : null)}
                        openSettings={() => handleOpenSettings(account)}
                        openDeleteDialog={() => handleOpenDeleteDialog(account)}
                        openRestoreDialog={() => handleOpenRestoreDialog(account)}
                        isDeleted={isDeleted}
                        canPermanentDelete={canPermanentDelete}
                        daysUntilPermanentDeleteAllowed={daysRemaining}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

    <SettingsDialog 
      isopen={isSettingsDialogOpen} 
      setIsOpen={(open) => {
        setIsSettingsDialogOpen(open);
        if (!open) {
          setOpenDropdownId(null); 
          setSelectedAccount(null); 
        }
      }} 
      account={selectedAccount} 
      availablePlans={availablePlans || [] as any} 
    />

    <DeleteDialog
      user_id={selectedAccount?.user_id || ''}
      email={selectedAccount?.email || ''}
      setIsOpen={(open) => setIsDeleteOpen(open)}
      isOpen={isDeleteOpen}
    />

    <RestoreDialog
      user_id={selectedAccount?.user_id || ''}
      email={selectedAccount?.email || ''}
      setIsOpen={(open) => setIsRestoreOpen(open)}
      isOpen={isRestoreOpen}
    />

  </Card>
  )
}